import { useState, useEffect, useMemo } from 'react'
import { ChevronRight, ChevronDown, Factory, Loader2, Calendar, FileText, Search } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { api } from '../../../lib/api'
import { useLanguage } from '../../../context/LanguageContext'
import { usePermissions } from '../../../hooks/usePermissions'
import { useFactory } from '../../../context/FactoryContext'
import { useTerminal } from '../../../context/TerminalContext'
import { findLineForTerminal, buildLineFromTerminal } from '../../../utils/terminal/terminalLine'
import RawEvents from '../components/RawEvents'
import CommitEvents from '../components/CommitEvents'

function Events() {
  const { t } = useLanguage()
  const { permissions, checkFactoryAccess } = usePermissions()
  const { selectedFactory } = useFactory()
  const { isHmiMode, terminal, loading: terminalLoading } = useTerminal()

  const [hierarchies, setHierarchies] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedNodes, setExpandedNodes] = useState(new Set())
  const [selectedLineId, setSelectedLineId] = useState(null)
  const [selectedLine, setSelectedLine] = useState(null)
  const [activeTab, setActiveTab] = useState('commit') // 'raw' or 'commit' - por defecto 'commit'
  const [searchQuery, setSearchQuery] = useState('')
  
  // Estado para redimensionamiento de la card de CommitEvents
  const [cardSize, setCardSize] = useState(() => {
    const saved = localStorage.getItem('commitEventsCardSize')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch (e) {
        return { width: 'auto', height: 'calc(100vh-200px)' }
      }
    }
    return { width: 'auto', height: 'calc(100vh-200px)' }
  })
  const [isResizing, setIsResizing] = useState(false)
  const [resizeDirection, setResizeDirection] = useState(null) // 'width', 'height', 'both'

  // Siempre cargar jerarquía (HMI la necesita para resolver la línea del terminal).
  // Solo resetear selección manual fuera de modo terminal.
  useEffect(() => {
    if (!isHmiMode) {
      setSelectedLineId(null)
      setSelectedLine(null)
      setExpandedNodes(new Set())
      setSearchQuery('')
    }
    loadHierarchies()
  }, [selectedFactory, isHmiMode])

  // Construir jerarquía desde la lista de assets
  const buildHierarchy = (assets) => {
    const hierarchies = {}
    
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      return hierarchies
    }
    
    const normalizeAsset = (asset) => {
      return {
        id: asset.ID !== undefined ? asset.ID : asset.id,
        parentId: asset.ParentID !== undefined ? asset.ParentID : asset.ParentId !== undefined ? asset.ParentId : asset.parentId !== undefined ? asset.parentId : asset.parent_id,
        code: asset.Code !== undefined ? asset.Code : asset.code,
        name: asset.Name !== undefined ? asset.Name : asset.name,
        location: asset.Location !== undefined ? asset.Location : asset.location,
        factory: asset.Factory !== undefined ? asset.Factory : asset.factory,
        hierarchicalLevel: asset.HierarchicalLevel !== undefined ? asset.HierarchicalLevel : (asset.hierarchicalLevel !== undefined ? asset.hierarchicalLevel : asset.hierarchical_level),
        ...asset
      }
    }
    
    const normalizedAssets = assets.map(normalizeAsset)
    const assetMap = new Map()
    normalizedAssets.forEach(asset => {
      if (asset.id != null) {
        assetMap.set(asset.id, { ...asset, children: [] })
      }
    })
    
    const rootNodes = []
    normalizedAssets.forEach(asset => {
      if (asset.id != null && (asset.parentId == null || asset.parentId === null || asset.parentId === '')) {
        rootNodes.push(asset)
      }
    })
    
    const buildTree = (asset, factoryCode = null) => {
      const node = assetMap.get(asset.id)
      if (!node) return null
      
      // Si este es un nodo raíz, usar su código como factory
      const currentFactoryCode = factoryCode || asset.code || asset.factory || asset.Code || asset.Factory
      
      // Propagar el factory code al nodo
      if (currentFactoryCode) {
        node.factory = currentFactoryCode
        node.Factory = currentFactoryCode
      }
      
      const children = normalizedAssets
        .filter(child => {
          const childParentId = child.parentId
          return childParentId != null && childParentId === asset.id
        })
        .map(child => buildTree(child, currentFactoryCode))
        .filter(Boolean)
      
      if (children.length > 0) {
        node.children = children
      }
      
      return node
    }
    
    rootNodes.forEach(rootAsset => {
      const tree = buildTree(rootAsset)
      if (tree) {
        const factoryCode = rootAsset.code || rootAsset.factory || rootAsset.Code || rootAsset.Factory || `ROOT_${rootAsset.id}`
        hierarchies[factoryCode] = tree
      }
    })
    
    const sortChildren = (node) => {
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => {
          const aCode = (a.code || '').toString()
          const bCode = (b.code || '').toString()
          return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base' })
        })
        node.children.forEach(sortChildren)
      }
    }
    
    Object.values(hierarchies).forEach(sortChildren)
    
    return hierarchies
  }

  const loadHierarchies = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get('/api/asset/list')
      
      let assets = []
      if (Array.isArray(response)) {
        assets = response
      } else if (response && Array.isArray(response.data)) {
        assets = response.data
      } else if (response && response.assets && Array.isArray(response.assets)) {
        assets = response.assets
      } else if (response && typeof response === 'object') {
        const keys = Object.keys(response)
        const arrayKey = keys.find(key => Array.isArray(response[key]))
        if (arrayKey) {
          assets = response[arrayKey]
        }
      }
      
      if (assets.length === 0) {
        setHierarchies({})
        setLoading(false)
        return
      }
      
      let filteredAssets = assets
      
      const hierarchies = buildHierarchy(filteredAssets)
      
      if (selectedFactory && hierarchies[selectedFactory]) {
        setHierarchies({ [selectedFactory]: hierarchies[selectedFactory] })
      } else {
        setHierarchies(hierarchies)
      }
      
      setLoading(false)
    } catch (err) {
      console.error('Error loading hierarchies:', err)
      setError(err.message || 'Error loading asset hierarchy')
      setLoading(false)
    }
  }

  // Filtrar nodos por búsqueda (mismo criterio que JerarquiaActivos: devuelve árbol filtrado)
  const filterNode = (node) => {
    if (!node) return null
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        (node.code && node.code.toLowerCase().includes(searchLower)) ||
        (node.name && node.name.toLowerCase().includes(searchLower)) ||
        (node.location && node.location.toLowerCase().includes(searchLower))
      if (matchesSearch) {
        return { ...node, children: node.children?.map(filterNode).filter(Boolean) }
      }
      if (node.children && node.children.length > 0) {
        const filteredChildren = node.children.map(filterNode).filter(Boolean)
        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren }
        }
      }
      return null
    }
    return { ...node, children: node.children?.map(filterNode).filter(Boolean) }
  }

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }

  const expandAll = () => {
    const nodeIds = new Set()
    Object.values(hierarchies).forEach((root) => {
      const collectIds = (n) => {
        nodeIds.add(n.id)
        if (n.children) n.children.forEach((child) => collectIds(child))
      }
      collectIds(root)
    })
    setExpandedNodes(nodeIds)
  }

  const collapseAll = () => {
    setExpandedNodes(new Set())
  }

  const getLevelColor = (level) => {
    switch (level) {
      case 0: return 'text-blue-400'
      case 1: return 'text-green-400'
      case 2: return 'text-yellow-400'
      default: return 'text-gray-300'
    }
  }

  // Detectar si un nodo es una línea de producción
  const isProductionLine = (node) => {
    if (!node) return false
    
    // Opción 1: Verificar hierarchicalLevel como array con 2 elementos (Fábrica, Línea)
    if (node.hierarchicalLevel && Array.isArray(node.hierarchicalLevel)) {
      const isLine = node.hierarchicalLevel.length === 2
      if (isLine) {
        console.log('✅ [Events] Línea detectada por hierarchicalLevel array:', node.code, node.hierarchicalLevel)
      }
      return isLine
    }
    
    // Opción 2: Verificar hierarchicalLevel como string
    if (node.hierarchicalLevel) {
      const level = String(node.hierarchicalLevel).toUpperCase()
      if (level === 'LINE' || level.includes('LINE')) {
        console.log('✅ [Events] Línea detectada por hierarchicalLevel string:', node.code, level)
        return true
      }
    }
    
    // Opción 3: Verificar por código que empiece con "Line_" o contenga "LINE"
    const code = (node.code || '').toString()
    const codeUpper = code.toUpperCase()
    if (codeUpper.startsWith('LINE_') || codeUpper.includes('LINE')) {
      console.log('✅ [Events] Línea detectada por código:', code)
      return true
    }
    
    // Opción 4: Verificar si el código empieza con "Line" (sin guión bajo)
    if (codeUpper.startsWith('LINE')) {
      console.log('✅ [Events] Línea detectada por código que empieza con LINE:', code)
      return true
    }
    
    return false
  }

  // Función helper para encontrar el factory desde el árbol
  const findFactoryFromHierarchy = (node) => {
    if (!node) return ''
    
    // Opción 1: Si hierarchicalLevel es un array, el primer elemento es el factory
    if (node.hierarchicalLevel && Array.isArray(node.hierarchicalLevel) && node.hierarchicalLevel.length > 0) {
      const factory = node.hierarchicalLevel[0]
      if (factory) {
        console.log('✅ [Events] Factory encontrado desde hierarchicalLevel array:', factory)
        return factory
      }
    }
    
    // Opción 2: Si tiene campo factory directo
    if (node.factory || node.Factory) {
      const factory = node.factory || node.Factory
      console.log('✅ [Events] Factory encontrado desde campo directo:', factory)
      return factory
    }
    
    // Opción 3: Buscar en el árbol de jerarquías
    // El factory es el nodo raíz (nivel 0) que contiene esta línea
    for (const [factoryKey, factoryNode] of Object.entries(hierarchies)) {
      // Buscar recursivamente si esta línea está dentro de esta fábrica
      const findNodeInTree = (treeNode) => {
        if (treeNode && treeNode.id === node.id) {
          return true
        }
        if (treeNode && treeNode.children && Array.isArray(treeNode.children)) {
          return treeNode.children.some(child => findNodeInTree(child))
        }
        return false
      }
      
      if (findNodeInTree(factoryNode)) {
        const foundFactory = factoryKey || factoryNode.code || factoryNode.Code || ''
        console.log('✅ [Events] Factory encontrado desde árbol de jerarquías:', foundFactory)
        return foundFactory
      }
    }
    
    console.warn('⚠️ [Events] Factory no encontrado para nodo:', {
      id: node.id,
      code: node.code,
      hierarchicalLevel: node.hierarchicalLevel
    })
    return ''
  }

  const handleLineSelect = async (line) => {
    // Enriquecer el objeto line con el factory si no lo tiene
    const enrichedLine = {
      ...line,
      factory: line.factory || line.Factory || findFactoryFromHierarchy(line)
    }
    
    console.log('🔵 [Events] Línea/nodo seleccionado:', {
      id: enrichedLine.id,
      code: enrichedLine.code,
      name: enrichedLine.name,
      factory: enrichedLine.factory,
      hierarchicalLevel: enrichedLine.hierarchicalLevel,
      isLine: isProductionLine(enrichedLine)
    })
    
    setSelectedLineId(enrichedLine.id)
    setSelectedLine(enrichedLine)
    setActiveTab('commit') // Mostrar Commit Events por defecto cuando se selecciona una nueva línea
    
    // Cargar detalles completos del asset para obtener ProductTypeName
    if (enrichedLine.id) {
      try {
        const assetDetails = await api.get(`/api/asset/showdetail/${enrichedLine.id}`)
        if (assetDetails) {
          // Enriquecer el objeto line con los detalles completos del asset
          const fullyEnrichedLine = {
            ...enrichedLine,
            Product: assetDetails.Product || assetDetails.product || assetDetails.mr_products,
            ProductType: assetDetails.Product?.ProductType || assetDetails.product?.productType || assetDetails.mr_products?.ProductType,
            ProductTypeName: assetDetails.Product?.ProductTypeName || assetDetails.Product?.ProductType?.Name || assetDetails.productTypeName || assetDetails.ProductTypeName
          }
          console.log('✅ [Events] Asset details cargados, ProductTypeName:', fullyEnrichedLine.ProductTypeName)
          setSelectedLine(fullyEnrichedLine)
        }
      } catch (err) {
        console.warn('⚠️ [Events] No se pudieron cargar los detalles del asset:', err)
        // Continuar sin los detalles, el componente CommitEvents intentará obtener el productTypeName de otras formas
      }
    }
  }

  // Renderizar nodo del árbol (mismo diseño que JerarquiaActivos)
  const renderTreeNode = (node, level = 0) => {
    const nodeId = node.id
    const isExpanded = expandedNodes.has(nodeId)
    const isSelected = selectedLineId === nodeId
    const hasChildren = node.children && node.children.length > 0
    const isLine = isProductionLine(node)
    const indent = level * 16
    const basePadding = 5

    return (
      <div key={nodeId} className="select-none">
        <div
          className={`flex items-center gap-1 px-1 py-0.5 hover:bg-gray-800/50 rounded cursor-pointer group transition-colors ${
            isSelected ? 'bg-blue-500/20 border-l-2 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${basePadding + indent}px` }}
          onClick={() => {
            if (hasChildren) toggleNode(nodeId)
            handleLineSelect(node)
          }}
          onDoubleClick={(e) => {
            e.stopPropagation()
            handleLineSelect(node)
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />
            )
          ) : (
            <div className="w-3 flex-shrink-0" />
          )}
          {isLine ? (
            <Factory size={12} className="text-blue-400 flex-shrink-0" />
          ) : (
            <FileText size={12} className="text-gray-400 flex-shrink-0" />
          )}
          <span
            className={`flex-1 min-w-0 truncate text-xs transition-colors ${
              isSelected ? 'text-blue-400 font-semibold' : getLevelColor(level)
            }`}
          >
            {node.code || node.name || `Asset ${nodeId}`}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderFullTree = () => {
    const factoryList = Object.keys(hierarchies).sort()
    return (
      <div className="space-y-0.5">
        {factoryList.map((factoryCode) => {
          const factory = hierarchies[factoryCode]
          const filteredFactory = filterNode(factory)
          if (filteredFactory) {
            return (
              <div key={factoryCode}>
                <div className="text-xs font-semibold text-gray-400 mb-1 px-1 pt-1">
                  {factoryCode}
                </div>
                {renderTreeNode(filteredFactory, 0)}
              </div>
            )
          }
          return null
        })}
      </div>
    )
  }

  // Filtrar líneas del árbol
  const getLines = (node, lines = []) => {
    if (isProductionLine(node)) {
      lines.push(node)
    }
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => getLines(child, lines))
    }
    return lines
  }

  const allLines = useMemo(() => {
    const lines = []
    Object.values(hierarchies).forEach(root => {
      getLines(root, lines)
    })
    return lines
  }, [hierarchies])

  useEffect(() => {
    if (!isHmiMode || !terminal?.bound || selectedLine || loading || terminalLoading) return

    const matchedLine = findLineForTerminal(allLines, terminal)
    if (matchedLine) {
      handleLineSelect(matchedLine)
      return
    }

    const fallbackLine = buildLineFromTerminal(terminal)
    if (fallbackLine) {
      handleLineSelect({
        ...fallbackLine,
        factory: terminal.factory,
        hierarchicalLevel: [terminal.factory, terminal.prodLine],
      })
    }
  }, [isHmiMode, terminal, allLines, selectedLine, loading, terminalLoading])

  const isWaitingForTerminalLine =
    isHmiMode && !selectedLine && (loading || terminalLoading)

  if (loading || isWaitingForTerminalLine) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-6">
          <div className="text-red-500">Error: {error}</div>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Calendar className="text-blue-400" />
          Events Management
        </h1>
      </div>

      <div className="flex h-[calc(100vh-200px)] gap-4 flex-1 min-h-0">
        {/* Jerarquía manual: oculta en HMI (línea fijada por terminal) */}
        {!isHmiMode && (
        <div className="flex-shrink-0 bg-gray-900/50 rounded-lg border border-gray-800 overflow-hidden flex flex-col w-64 lg:w-72">
          <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Factory size={16} />
                Jerarquía
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">Selecciona una línea</p>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={expandAll}
                className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
                title="Expandir todo"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={collapseAll}
                className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
                title="Colapsar todo"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <div className="px-2 py-1.5 border-b border-gray-800">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto py-1">
            {Object.keys(hierarchies).length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                No hay datos disponibles
              </div>
            ) : (
              renderFullTree()
            )}
          </div>
        </div>
        )}

        {/* Panel de Events */}
        <div 
          className={`flex-1 min-w-0 relative ${isResizing ? 'select-none' : ''}`}
          style={{
            width: cardSize.width === 'auto' ? 'auto' : `${cardSize.width}px`,
            height: cardSize.height === 'calc(100vh-200px)' ? 'calc(100vh-200px)' : `${cardSize.height}px`,
            minWidth: '400px',
            minHeight: '300px',
            maxWidth: '100%',
            maxHeight: '100vh',
            transition: isResizing ? 'none' : 'width 0.2s ease, height 0.2s ease'
          }}
        >
          {selectedLine ? (
            <Card 
              className="p-4 overflow-hidden flex flex-col relative"
              style={{
                width: '100%',
                height: '100%'
              }}
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-white mb-2">
                  {selectedLine.code || selectedLine.line} - {selectedLine.name || terminal?.name || selectedLine.code}
                </h2>
                {isHmiMode && terminal?.prodLine && (
                  <p className="text-sm text-gray-400 mb-2">
                    Terminal: {terminal.name || terminal.prodLine} · {terminal.factory}
                  </p>
                )}
                
                {/* Tabs */}
                <div className="flex gap-2 border-b border-gray-700">
                  <button
                    onClick={() => setActiveTab('raw')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeTab === 'raw'
                        ? 'text-blue-400 border-b-2 border-blue-400'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Raw Events
                  </button>
                  <button
                    onClick={() => setActiveTab('commit')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeTab === 'commit'
                        ? 'text-blue-400 border-b-2 border-blue-400'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Commit Events
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {activeTab === 'raw' ? (
                  <RawEvents 
                    lineId={selectedLineId} 
                    line={selectedLine}
                    onCommitSuccess={() => setActiveTab('commit')}
                  />
                ) : (
                  <CommitEvents lineId={selectedLineId} line={selectedLine} />
                )}
              </div>
              
              {/* Handles de redimensionamiento */}
              {activeTab === 'commit' && (
                <>
                  {/* Handle esquina inferior derecha (ambos) */}
                  <div
                    className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-blue-600 hover:bg-blue-500 opacity-50 hover:opacity-100 transition-opacity z-10 rounded-tl-lg"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setIsResizing(true)
                      setResizeDirection('both')
                      const startX = e.clientX
                      const startY = e.clientY
                      const startWidth = cardSize.width === 'auto' ? 800 : cardSize.width
                      const startHeight = cardSize.height === 'calc(100vh-200px)' ? window.innerHeight - 200 : cardSize.height
                      
                      let currentSize = { width: startWidth, height: startHeight }
                      
                      const handleMouseMove = (e) => {
                        const newWidth = Math.max(400, Math.min(window.innerWidth - 300, startWidth + (e.clientX - startX)))
                        const newHeight = Math.max(300, Math.min(window.innerHeight - 100, startHeight + (e.clientY - startY)))
                        currentSize = { width: newWidth, height: newHeight }
                        setCardSize(currentSize)
                      }
                      
                      const handleMouseUp = () => {
                        setIsResizing(false)
                        setResizeDirection(null)
                        localStorage.setItem('commitEventsCardSize', JSON.stringify(currentSize))
                        document.removeEventListener('mousemove', handleMouseMove)
                        document.removeEventListener('mouseup', handleMouseUp)
                      }
                      
                      document.addEventListener('mousemove', handleMouseMove)
                      document.addEventListener('mouseup', handleMouseUp)
                    }}
                    title="Redimensionar (arrastrar)"
                  />
                  
                  {/* Handle borde derecho (ancho) */}
                  <div
                    className="absolute top-0 right-0 w-2 h-full cursor-ew-resize hover:bg-blue-500 opacity-0 hover:opacity-30 transition-opacity z-10"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setIsResizing(true)
                      setResizeDirection('width')
                      const startX = e.clientX
                      const startWidth = cardSize.width === 'auto' ? 800 : cardSize.width
                      
                      let currentSize = { ...cardSize, width: startWidth }
                      
                      const handleMouseMove = (e) => {
                        const newWidth = Math.max(400, Math.min(window.innerWidth - 300, startWidth + (e.clientX - startX)))
                        currentSize = { ...cardSize, width: newWidth }
                        setCardSize(currentSize)
                      }
                      
                      const handleMouseUp = () => {
                        setIsResizing(false)
                        setResizeDirection(null)
                        localStorage.setItem('commitEventsCardSize', JSON.stringify(currentSize))
                        document.removeEventListener('mousemove', handleMouseMove)
                        document.removeEventListener('mouseup', handleMouseUp)
                      }
                      
                      document.addEventListener('mousemove', handleMouseMove)
                      document.addEventListener('mouseup', handleMouseUp)
                    }}
                    title="Ajustar ancho"
                  />
                  
                  {/* Handle borde inferior (alto) */}
                  <div
                    className="absolute bottom-0 left-0 w-full h-2 cursor-ns-resize hover:bg-blue-500 opacity-0 hover:opacity-30 transition-opacity z-10"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setIsResizing(true)
                      setResizeDirection('height')
                      const startY = e.clientY
                      const startHeight = cardSize.height === 'calc(100vh-200px)' ? window.innerHeight - 200 : cardSize.height
                      
                      let currentSize = { ...cardSize, height: startHeight }
                      
                      const handleMouseMove = (e) => {
                        const newHeight = Math.max(300, Math.min(window.innerHeight - 100, startHeight + (e.clientY - startY)))
                        currentSize = { ...cardSize, height: newHeight }
                        setCardSize(currentSize)
                      }
                      
                      const handleMouseUp = () => {
                        setIsResizing(false)
                        setResizeDirection(null)
                        localStorage.setItem('commitEventsCardSize', JSON.stringify(currentSize))
                        document.removeEventListener('mousemove', handleMouseMove)
                        document.removeEventListener('mouseup', handleMouseUp)
                      }
                      
                      document.addEventListener('mousemove', handleMouseMove)
                      document.addEventListener('mouseup', handleMouseUp)
                    }}
                    title="Ajustar alto"
                  />
                  
                  {/* Botón para resetear tamaño */}
                  <button
                    onClick={() => {
                      setCardSize({ width: 'auto', height: 'calc(100vh-200px)' })
                      localStorage.removeItem('commitEventsCardSize')
                    }}
                    className="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded opacity-0 hover:opacity-100 transition-opacity z-10"
                    title="Resetear tamaño"
                  >
                    ↺ Reset
                  </button>
                </>
              )}
            </Card>
          ) : (
            <Card className="p-8 flex items-center justify-center" style={{ width: '100%', height: '100%' }}>
              <div className="text-center text-gray-400">
                <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a production line to view events</p>
                <p className="text-sm mt-2 text-gray-500">
                  Click on any line in the asset tree to view Raw Events and Commit Events
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default Events


